import React from 'react';

/**
 * Diving Operations Form Component
 */
const DivingOperationsForm = ({ formData, setFormData, onSubmit, onCancel }) => {
  const calculateDuration = (start, end) => {
    if (!start || !end) return '';
    const startMin = parseInt(start.split(':')[0]) * 60 + parseInt(start.split(':')[1]);
    const endMin = parseInt(end.split(':')[0]) * 60 + parseInt(end.split(':')[1]);
    const duration = endMin - startMin;
    if (duration < 0) return '';
    return `${Math.floor(duration / 60)}h ${duration % 60}m`;
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b">
        <h3 className="text-sm font-medium text-gray-900">Record Diving Operation</h3>
      </div>
      <form onSubmit={onSubmit} className="p-6 space-y-4">
        {/* Date/Time Row */}
        <div className="grid grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Start Time</label>
            <input
              type="time"
              value={formData.startTime}
              onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">End Time</label>
            <input
              type="time"
              value={formData.endTime}
              onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Dive Type</label>
            <select
              value={formData.diveType}
              onChange={(e) => setFormData({ ...formData, diveType: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="commercial">Commercial</option>
              <option value="survey">Survey</option>
              <option value="inspection">Inspection</option>
              <option value="salvage">Salvage</option>
              <option value="construction">Construction</option>
            </select>
          </div>
        </div>
        
        {/* Location and Depth */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="Site name or description"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Max Depth (m)</label>
            <input
              type="number"
              step="0.1"
              value={formData.depth}
              onChange={(e) => setFormData({ ...formData, depth: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Divers Deployed</label>
            <input
              type="number"
              min="1"
              value={formData.diversDeployed}
              onChange={(e) => setFormData({ ...formData, diversDeployed: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              required
            />
          </div>
        </div>
        
        {/* Personnel */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Dive Supervisor</label>
            <input
              type="text"
              value={formData.diveSupervisor}
              onChange={(e) => setFormData({ ...formData, diveSupervisor: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Standby Diver</label>
            <input
              type="text"
              value={formData.standbyDiver}
              onChange={(e) => setFormData({ ...formData, standbyDiver: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              required
            />
          </div>
        </div>
        
        {/* Equipment */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-gray-700">Equipment Used</label>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.equipment.scuba}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  equipment: { ...formData.equipment, scuba: e.target.checked }
                })}
              />
              <span className="text-sm">SCUBA</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.equipment.surfaceSupplied}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  equipment: { ...formData.equipment, surfaceSupplied: e.target.checked }
                })}
              />
              <span className="text-sm">Surface Supplied</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.equipment.bellDiving}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  equipment: { ...formData.equipment, bellDiving: e.target.checked }
                })}
              />
              <span className="text-sm">Bell Diving</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.decompression}
                onChange={(e) => setFormData({ ...formData, decompression: e.target.checked })}
              />
              <span className="text-sm">Decompression Required</span>
            </label>
          </div>
        </div>
        
        {/* Environmental Conditions */}
        <div className="grid grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Water Temp (°C)</label>
            <input
              type="number"
              value={formData.waterTemp}
              onChange={(e) => setFormData({ ...formData, waterTemp: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Visibility (m)</label>
            <input
              type="number"
              value={formData.visibility}
              onChange={(e) => setFormData({ ...formData, visibility: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Current (kts)</label>
            <input
              type="number"
              step="0.1"
              value={formData.current}
              onChange={(e) => setFormData({ ...formData, current: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Sea State</label>
            <select
              value={formData.seaState}
              onChange={(e) => setFormData({ ...formData, seaState: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="">Select...</option>
              <option value="calm">Calm (0-1)</option>
              <option value="slight">Slight (2-3)</option>
              <option value="moderate">Moderate (4-5)</option>
              <option value="rough">Rough (6-7)</option>
            </select>
          </div>
        </div>
        
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
          >
            Save Dive Log
          </button>
        </div>
      </form>
    </div>
  );
};

export default DivingOperationsForm;